import React, { useContext, useState, useEffect, useRef } from 'react';
import LottieAnimation, { CorrectAnimation, WrongAnimation, SuccessAnimation, ConfettiAnimation } from '../components/LottieAnimation';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, Timer, Trophy, Zap, Play, 
  RotateCcw, Star, Flame, Check, X, ArrowLeft,
  Plane, AlertTriangle, Users, Code, BookOpen, Radio
} from 'lucide-react';

// ICAO Alphabet
const ICAO_ALPHABET = {
  'A': 'Alpha', 'B': 'Bravo', 'C': 'Charlie', 'D': 'Delta',
  'E': 'Echo', 'F': 'Foxtrot', 'G': 'Golf', 'H': 'Hotel',
  'I': 'India', 'J': 'Juliet', 'K': 'Kilo', 'L': 'Lima',
  'M': 'Mike', 'N': 'November', 'O': 'Oscar', 'P': 'Papa',
  'Q': 'Quebec', 'R': 'Romeo', 'S': 'Sierra', 'T': 'Tango',
  'U': 'Uniform', 'V': 'Victor', 'W': 'Whiskey', 'X': 'X-ray',
  'Y': 'Yankee', 'Z': 'Zulu'
};

// Similar sounding ICAO distractors for each letter
const ICAO_DISTRACTORS = {
  'A': ['Alfa', 'Apple', 'Adam', 'Able'],
  'B': ['Beta', 'Baker', 'Boy', 'Banana'],
  'C': ['Coca', 'Carlos', 'Cat', 'Cherry'],
  'D': ['David', 'Dog', 'Dancer', 'Denver'],
  'E': ['Edward', 'Easy', 'Elephant', 'Eagle'],
  'F': ['Fox', 'Frank', 'Father', 'Florida'],
  'G': ['George', 'Grape', 'Garden', 'Germany'],
  'H': ['Henry', 'House', 'Happy', 'Hawaii'],
  'I': ['Italy', 'Island', 'Ice', 'Ivan'],
  'J': ['John', 'Japan', 'James', 'Junior'],
  'K': ['King', 'Knife', 'Karen', 'Kenya'],
  'L': ['London', 'Love', 'Larry', 'Louis'],
  'M': ['Mary', 'Michael', 'Mother', 'Mexico'],
  'N': ['Nancy', 'Norway', 'Nice', 'Nora'],
  'O': ['Orange', 'Ocean', 'Oliver', 'Ohio'],
  'P': ['Peter', 'Paris', 'Paul', 'Patrick'],
  'Q': ['Queen', 'Quest', 'Quiet', 'Quality'],
  'R': ['Roger', 'Robert', 'Radio', 'Russia'],
  'S': ['Sugar', 'Sam', 'Spain', 'Snake'],
  'T': ['Tom', 'Tiger', 'Texas', 'Turkey'],
  'U': ['Uncle', 'United', 'Utah', 'Union'],
  'V': ['Venus', 'Violet', 'Vienna', 'Victory'],
  'W': ['William', 'Water', 'Winter', 'Wilson'],
  'X': ['X-men', 'Xerox', 'Xavier', 'Xena'],
  'Y': ['Yellow', 'York', 'Young', 'Yoga'],
  'Z': ['Zebra', 'Zero', 'Zeus', 'Zoom']
};

// CATEGORY 1: Vocabulary - Aviation Terms (20 questions)
const VOCABULARY = [
  { en: 'Aircraft', tr: 'Uçak' },
  { en: 'Cabin', tr: 'Kabin' },
  { en: 'Crew', tr: 'Mürettebat' },
  { en: 'Passenger', tr: 'Yolcu' },
  { en: 'Seat', tr: 'Koltuk' },
  { en: 'Aisle', tr: 'Koridor' },
  { en: 'Emergency', tr: 'Acil Durum' },
  { en: 'Seatbelt', tr: 'Emniyet Kemeri' },
  { en: 'Life vest', tr: 'Can Yeleği' },
  { en: 'Oxygen mask', tr: 'Oksijen Maskesi' },
  { en: 'Departure', tr: 'Kalkış' },
  { en: 'Landing', tr: 'İniş' },
  { en: 'Turbulence', tr: 'Türbülans' },
  { en: 'Gate', tr: 'Kapı' },
  { en: 'Boarding', tr: 'Biniş' },
  { en: 'Captain', tr: 'Kaptan' },
  { en: 'Cockpit', tr: 'Kokpit' },
  { en: 'Galley', tr: 'Mutfak' },
  { en: 'Lavatory', tr: 'Tuvalet' },
  { en: 'Overhead bin', tr: 'Üst Bagaj' }
];

// CATEGORY 2: Emergency Procedures (20 questions)
const EMERGENCY_PROCEDURES = [
  { question: 'Uçakta yangın çıktığında ilk yapılacak işlem nedir?', answer: 'Yangın söndürücü kullanmak', options: ['Panik yapmak', 'Kapıyı açmak', 'Yangın söndürücü kullanmak', 'Beklemek'] },
  { question: 'Acil iniş pozisyonu nasıl alınır?', answer: 'Başı dizlere eğip, elleri başın arkasına koymak', options: ['Ayakta durmak', 'Başı dizlere eğip, elleri başın arkasına koymak', 'Koşmak', 'Yatmak'] },
  { question: 'Kabin basıncı düştüğünde hangi maske kullanılır?', answer: 'Oksijen maskesi', options: ['Gaz maskesi', 'Oksijen maskesi', 'Cerrahi maske', 'Dalgıç maskesi'] },
  { question: 'Acil tahliyede yolculara ne söylenir?', answer: 'Sakin olun ve çıkışları takip edin', options: ['Koşun', 'Sakin olun ve çıkışları takip edin', 'Eşyalarınızı alın', 'Bekleyin'] },
  { question: 'Can yeleği ne zaman şişirilir?', answer: 'Uçaktan çıktıktan sonra', options: ['Hemen', 'Uçaktan çıktıktan sonra', 'Kalkışta', 'İnişte'] },
  { question: 'Slide/Raft ne için kullanılır?', answer: 'Acil tahliye ve su üzerinde kalma', options: ['Eğlence', 'Acil tahliye ve su üzerinde kalma', 'Kargo taşıma', 'Oturma'] },
  { question: 'Megafon acil durumda ne için kullanılır?', answer: 'Yolculara talimat vermek', options: ['Müzik çalmak', 'Yolculara talimat vermek', 'Pilota haber vermek', 'Dekorasyon'] },
  { question: 'Acil çıkış kapısı yanında kim oturtulmamalıdır?', answer: 'Çocuklar ve engelliler', options: ['Erkekler', 'Kadınlar', 'Çocuklar ve engelliler', 'Yaşlılar'] },
  { question: 'First Aid Kit nerede bulunur?', answer: 'Kabin içinde belirli noktalarda', options: ['Kokpitte', 'Kabin içinde belirli noktalarda', 'Bagajda', 'Tuvalette'] },
  { question: 'Smoke Hood ne işe yarar?', answer: 'Dumandan korunma', options: ['Yağmurdan korunma', 'Dumandan korunma', 'Soğuktan korunma', 'Gürültüden korunma'] },
  { question: 'Acil durum aydınlatması ne renktir?', answer: 'Yeşil ve beyaz', options: ['Kırmızı', 'Yeşil ve beyaz', 'Mavi', 'Sarı'] },
  { question: 'Decompression durumunda ilk ne yapılır?', answer: 'Kendi oksijen maskenizi takın', options: ['Yolculara yardım edin', 'Kendi oksijen maskenizi takın', 'Kaçın', 'Oturun'] },
  { question: 'Ditching nedir?', answer: 'Suya zorunlu iniş', options: ['Normal iniş', 'Suya zorunlu iniş', 'Havada durma', 'İptal'] },
  { question: 'Brace position ne demek?', answer: 'Koruma pozisyonu', options: ['Dinlenme pozisyonu', 'Koruma pozisyonu', 'Uyku pozisyonu', 'Spor pozisyonu'] },
  { question: 'PBE (Protective Breathing Equipment) ne için kullanılır?', answer: 'Duman ve zehirli gazlardan korunma', options: ['Nefes egzersizi', 'Duman ve zehirli gazlardan korunma', 'Dalış', 'Spor'] },
  { question: 'Halon yangın söndürücü hangi yangın tipinde kullanılır?', answer: 'Elektrik yangınları', options: ['Kağıt yangını', 'Elektrik yangınları', 'Yağ yangını', 'Gaz yangını'] },
  { question: 'ELT (Emergency Locator Transmitter) ne işe yarar?', answer: 'Acil durum sinyali gönderme', options: ['Müzik çalma', 'Acil durum sinyali gönderme', 'Işık verme', 'Isıtma'] },
  { question: 'Crew rest area acil durumda ne olur?', answer: 'Tahliye edilir', options: ['Kilitlenir', 'Tahliye edilir', 'Kullanılmaya devam edilir', 'Gizlenir'] },
  { question: 'Survival kit içinde ne bulunmaz?', answer: 'Televizyon', options: ['Su', 'Televizyon', 'İlk yardım malzemesi', 'Düdük'] },
  { question: 'Acil tahliye süresi hedefi kaç saniyedir?', answer: '90 saniye', options: ['30 saniye', '90 saniye', '5 dakika', '10 dakika'] }
];

// CATEGORY 3: Aircraft Types and Features (20 questions)
const AIRCRAFT_TYPES = [
  { question: 'Boeing 737 kaç yolcu kapasitelidir (ortalama)?', answer: '150-189 yolcu', options: ['50-80 yolcu', '150-189 yolcu', '300-400 yolcu', '500+ yolcu'] },
  { question: 'Airbus A380 kaç katlıdır?', answer: '2 katlı', options: ['1 katlı', '2 katlı', '3 katlı', '4 katlı'] },
  { question: 'Narrow-body uçak ne demek?', answer: 'Tek koridorlu uçak', options: ['Dar kanat', 'Tek koridorlu uçak', 'Küçük motor', 'Kısa mesafe'] },
  { question: 'Wide-body uçak örneği hangisidir?', answer: 'Boeing 777', options: ['Boeing 737', 'Boeing 777', 'Airbus A320', 'Embraer 190'] },
  { question: 'Winglet ne işe yarar?', answer: 'Yakıt tasarrufu sağlar', options: ['Süs için', 'Yakıt tasarrufu sağlar', 'Hız azaltır', 'Ses çıkarır'] },
  { question: 'APU (Auxiliary Power Unit) ne işe yarar?', answer: 'Yerde enerji sağlar', options: ['Uçuşta kullanılır', 'Yerde enerji sağlar', 'Bagaj taşır', 'Yolcu eğlendirir'] },
  { question: 'Galley uçağın hangi bölümüdür?', answer: 'Mutfak', options: ['Kokpit', 'Mutfak', 'Tuvalet', 'Bagaj'] },
  { question: 'Jumpseat kimin için ayrılmıştır?', answer: 'Kabin ekibi', options: ['Yolcular', 'Kabin ekibi', 'Çocuklar', 'VIP'] },
  { question: 'Boeing 787 lakabı nedir?', answer: 'Dreamliner', options: ['Jumbo', 'Dreamliner', 'Super', 'Star'] },
  { question: 'Airbus A350 gövdesi hangi malzemedendir?', answer: 'Karbon fiber kompozit', options: ['Alüminyum', 'Karbon fiber kompozit', 'Çelik', 'Plastik'] },
  { question: 'Business class genelde uçağın neresindedir?', answer: 'Ön taraf', options: ['Arka', 'Ön taraf', 'Orta', 'Kanat üstü'] },
  { question: 'Overwing exit ne demek?', answer: 'Kanat üstü çıkışı', options: ['Ön kapı', 'Kanat üstü çıkışı', 'Arka kapı', 'Acil fren'] },
  { question: 'Lavatory lock indicator ne gösterir?', answer: 'Tualetin dolu/boş olduğunu', options: ['Kapı kilidini', 'Tualetin dolu/boş olduğunu', 'Acil durumu', 'Yangını'] },
  { question: 'IFE sistemi ne demek?', answer: 'In-Flight Entertainment', options: ['In-Flight Engine', 'In-Flight Entertainment', 'Internal Fire Exit', 'International Flight Entry'] },
  { question: 'Pressurization ne sağlar?', answer: 'Kabin basıncını', options: ['Hızı', 'Kabin basıncını', 'Yakıtı', 'Işığı'] },
  { question: 'Cargo hold nerededir?', answer: 'Uçağın altında', options: ['Üstte', 'Uçağın altında', 'Kanatta', 'Kokpitte'] },
  { question: 'PSU (Passenger Service Unit) nerede bulunur?', answer: 'Koltukların üstünde', options: ['Yerde', 'Koltukların üstünde', 'Tuvalette', 'Mutfakta'] },
  { question: 'Thrust reverser ne zaman kullanılır?', answer: 'İnişte fren için', options: ['Kalkışta', 'İnişte fren için', 'Havada', 'Park halinde'] },
  { question: 'Flight deck başka ne olarak bilinir?', answer: 'Kokpit', options: ['Kabin', 'Kokpit', 'Galley', 'Lavatory'] },
  { question: 'Regional jet örneği hangisidir?', answer: 'Embraer E175', options: ['Boeing 747', 'Embraer E175', 'Airbus A380', 'Boeing 777'] }
];

// CATEGORY 4: Passenger Services (20 questions)
const PASSENGER_SERVICES = [
  { question: 'UM yolcu ne demek?', answer: 'Refakatsiz çocuk', options: ['Hasta yolcu', 'Refakatsiz çocuk', 'VIP yolcu', 'Yaşlı yolcu'] },
  { question: 'PRM yolcu ne demek?', answer: 'Hareket kısıtlı yolcu', options: ['Premium yolcu', 'Hareket kısıtlı yolcu', 'Özel yolcu', 'Pilot'] },
  { question: 'Special meal ne zaman sipariş edilir?', answer: 'Uçuştan önce', options: ['Uçuşta', 'Uçuştan önce', 'İnişte', 'Bagaj alırken'] },
  { question: 'Vegetarian meal kodu nedir?', answer: 'VGML', options: ['AVML', 'VGML', 'KSML', 'DBML'] },
  { question: 'WCHR ne anlama gelir?', answer: 'Tekerlekli sandalye (rampa)', options: ['Yürüme güçlüğü', 'Tekerlekli sandalye (rampa)', 'Görme engelli', 'İşitme engelli'] },
  { question: 'Bassinet ne için kullanılır?', answer: 'Bebek yatağı', options: ['Bagaj', 'Bebek yatağı', 'Yemek', 'İlaç'] },
  { question: 'Duty-free satış ne zaman yapılır?', answer: 'Uçuş sırasında', options: ['Kalkıştan önce', 'Uçuş sırasında', 'İnişten sonra', 'Check-in\'de'] },
  { question: 'Hot towel ne zaman dağıtılır?', answer: 'Servis öncesi', options: ['İniş sonrası', 'Servis öncesi', 'Acil durumda', 'Kalkışta'] },
  { question: 'Welcome drink ne zaman sunulur?', answer: 'Biniş sırasında', options: ['İnişte', 'Biniş sırasında', 'Havada', 'Bagaj alırken'] },
  { question: 'Amenity kit genelde hangi sınıfta verilir?', answer: 'Business ve First Class', options: ['Ekonomi', 'Business ve First Class', 'Herkese', 'Hiç kimseye'] },
  { question: 'MAAS ne demek?', answer: 'Meet and Assist Service', options: ['Meal And Assist', 'Meet and Assist Service', 'Medical Aid', 'Maintenance'] },
  { question: 'Infant ne demek?', answer: '0-2 yaş bebek', options: ['Çocuk', '0-2 yaş bebek', 'Genç', 'Yetişkin'] },
  { question: 'Bulkhead seat avantajı nedir?', answer: 'Daha fazla bacak mesafesi', options: ['Ucuz fiyat', 'Daha fazla bacak mesafesi', 'Pencere manzarası', 'Sessizlik'] },
  { question: 'Exit row seat kısıtlaması nedir?', answer: '15 yaş altı oturamaz', options: ['Kadınlar oturamaz', '15 yaş altı oturamaz', 'Yaşlılar oturamaz', 'Kısıtlama yok'] },
  { question: 'Pre-boarding kimlere uygulanır?', answer: 'Özel yardım gerektiren yolcular', options: ['Herkes', 'Özel yardım gerektiren yolcular', 'Sadece erkekler', 'Son gelen'] },
  { question: 'Lost & Found ne demek?', answer: 'Kayıp eşya', options: ['Bulunan uçak', 'Kayıp eşya', 'Geç kalan', 'Erken gelen'] },
  { question: 'Seat assignment ne demek?', answer: 'Koltuk ataması', options: ['Koltuk temizliği', 'Koltuk ataması', 'Koltuk satışı', 'Koltuk tamiri'] },
  { question: 'Kosher meal kodu nedir?', answer: 'KSML', options: ['VGML', 'KSML', 'MOML', 'AVML'] },
  { question: 'Connecting passenger ne demek?', answer: 'Aktarmalı yolcu', options: ['Bağlantılı telefon', 'Aktarmalı yolcu', 'İnternete bağlı', 'VIP yolcu'] },
  { question: 'Lounge access kimin için?', answer: 'Business/First class ve üye yolcular', options: ['Herkes', 'Business/First class ve üye yolcular', 'Sadece pilot', 'Sadece ekip'] }
];

// CATEGORY 5: Aviation Codes and Abbreviations (20 questions)
const AVIATION_CODES = [
  { question: 'IST havalimanı kodu nerede?', answer: 'İstanbul', options: ['İzmir', 'İstanbul', 'Antalya', 'Ankara'] },
  { question: 'SAW havalimanı kodu nerede?', answer: 'Sabiha Gökçen', options: ['Antalya', 'Sabiha Gökçen', 'Samsun', 'Şanlıurfa'] },
  { question: 'AYT havalimanı kodu nerede?', answer: 'Antalya', options: ['Aydın', 'Antalya', 'Adana', 'Afyon'] },
  { question: 'ESB havalimanı kodu nerede?', answer: 'Ankara Esenboğa', options: ['Erzurum', 'Ankara Esenboğa', 'Eskişehir', 'Edirne'] },
  { question: 'ADB havalimanı kodu nerede?', answer: 'İzmir Adnan Menderes', options: ['Adana', 'İzmir Adnan Menderes', 'Antalya', 'Aydın'] },
  { question: 'ETA ne demek?', answer: 'Estimated Time of Arrival', options: ['Exit Time Alert', 'Estimated Time of Arrival', 'Emergency Take Action', 'Engine Test Approved'] },
  { question: 'ETD ne demek?', answer: 'Estimated Time of Departure', options: ['Emergency Test Done', 'Estimated Time of Departure', 'Engine Type Document', 'Exit Time Delay'] },
  { question: 'PAX ne anlama gelir?', answer: 'Passengers (Yolcular)', options: ['Paket', 'Passengers (Yolcular)', 'Pilot Assist', 'Park Area'] },
  { question: 'NOTAM ne demek?', answer: 'Notice to Air Missions', options: ['Not Available Machine', 'Notice to Air Missions', 'Normal Operation', 'Night Only Time'] },
  { question: 'METAR ne demek?', answer: 'Meteorological Report', options: ['Medical Report', 'Meteorological Report', 'Mechanical Test', 'Maintenance Record'] },
  { question: 'ATC ne demek?', answer: 'Air Traffic Control', options: ['Airport Terminal Check', 'Air Traffic Control', 'Aircraft Test Center', 'Airline Training Course'] },
  { question: 'SOP ne demek?', answer: 'Standard Operating Procedure', options: ['Safety Operation Plan', 'Standard Operating Procedure', 'Special Order Process', 'System Operation Program'] },
  { question: 'CRM ne demek?', answer: 'Crew Resource Management', options: ['Customer Relation Manager', 'Crew Resource Management', 'Cargo Room Management', 'Check Room Manual'] },
  { question: 'MEL ne demek?', answer: 'Minimum Equipment List', options: ['Maximum Engine Load', 'Minimum Equipment List', 'Medical Emergency Level', 'Maintenance Entry Log'] },
  { question: 'UTC ne demek?', answer: 'Coordinated Universal Time', options: ['United Time Control', 'Coordinated Universal Time', 'Universal Test Center', 'Unified Travel Code'] },
  { question: 'LHR havalimanı kodu nerede?', answer: 'Londra Heathrow', options: ['Los Angeles', 'Londra Heathrow', 'Las Vegas', 'Lisbon'] },
  { question: 'JFK havalimanı kodu nerede?', answer: 'New York', options: ['Japan', 'New York', 'Jamaica', 'Jordan'] },
  { question: 'CDG havalimanı kodu nerede?', answer: 'Paris Charles de Gaulle', options: ['Chicago', 'Paris Charles de Gaulle', 'Copenhagen', 'Cairo'] },
  { question: 'DXB havalimanı kodu nerede?', answer: 'Dubai', options: ['Delhi', 'Dubai', 'Dallas', 'Dublin'] },
  { question: 'FRA havalimanı kodu nerede?', answer: 'Frankfurt', options: ['France', 'Frankfurt', 'Florence', 'Finland'] }
];

// CATEGORY 6: Announcements and Communication (20 questions)  
const ANNOUNCEMENTS = [
  { question: '"Fasten your seatbelt" ne demek?', answer: 'Emniyet kemerinizi bağlayın', options: ['Koltuğunuza oturun', 'Emniyet kemerinizi bağlayın', 'Kapıyı kapatın', 'Işığı kapatın'] },
  { question: '"We are now ready for departure" ne demek?', answer: 'Kalkışa hazırız', options: ['İnişe hazırız', 'Kalkışa hazırız', 'Bekliyoruz', 'Gecikme var'] },
  { question: '"Please return to your seats" ne demek?', answer: 'Lütfen koltuklarınıza dönün', options: ['Lütfen ayağa kalkın', 'Lütfen koltuklarınıza dönün', 'Lütfen yürüyün', 'Lütfen bekleyin'] },
  { question: '"Cabin crew, prepare for landing" ne demek?', answer: 'Kabin ekibi, inişe hazırlanın', options: ['Kabin ekibi, kalkışa hazırlanın', 'Kabin ekibi, inişe hazırlanın', 'Kabin ekibi, servis yapın', 'Kabin ekibi, dinlenin'] },
  { question: '"We are experiencing turbulence" ne demek?', answer: 'Türbülansla karşılaşıyoruz', options: ['Hava güzel', 'Türbülansla karşılaşıyoruz', 'Gecikmemiz var', 'Motor arızası'] },
  { question: '"In case of emergency" ne demek?', answer: 'Acil durumda', options: ['Normal durumda', 'Acil durumda', 'Beklenmedik', 'Planlı'] },
  { question: '"The captain has turned on the seatbelt sign" ne demek?', answer: 'Kaptan emniyet kemeri işaretini yaktı', options: ['Kaptan ışıkları kapattı', 'Kaptan emniyet kemeri işaretini yaktı', 'Kaptan konuştu', 'Kaptan indi'] },
  { question: '"We will be serving beverages shortly" ne demek?', answer: 'Kısa süre içinde içecek servis edeceğiz', options: ['İçecek yok', 'Kısa süre içinde içecek servis edeceğiz', 'Yemek servisi', 'Satış yapacağız'] },
  { question: '"Please stow your luggage" ne demek?', answer: 'Lütfen bagajınızı yerleştirin', options: ['Bagajınızı alın', 'Lütfen bagajınızı yerleştirin', 'Bagaj kaybı', 'Bagaj ücreti'] },
  { question: '"Cross-check and report" ne demek?', answer: 'Kontrol edin ve rapor verin', options: ['Kapıları açın', 'Kontrol edin ve rapor verin', 'Servis yapın', 'Oturun'] },
  { question: '"Doors to manual" ne demek?', answer: 'Kapıları manuel moda alın', options: ['Kapıları açın', 'Kapıları manuel moda alın', 'Kapıları kilitleyin', 'Kapıları kontrol edin'] },
  { question: '"Arm doors and cross-check" ne demek?', answer: 'Kapıları silahlandırın ve kontrol edin', options: ['Kapıları açın', 'Kapıları silahlandırın ve kontrol edin', 'Kapıları kapatın', 'Kapıları temizleyin'] },
  { question: '"We have begun our descent" ne demek?', answer: 'Alçalmaya başladık', options: ['Yükseldik', 'Alçalmaya başladık', 'Bekledik', 'Hızlandık'] },
  { question: '"Flight attendants, please be seated" ne demek?', answer: 'Kabin memurları, lütfen oturun', options: ['Kabin memurları, ayağa kalkın', 'Kabin memurları, lütfen oturun', 'Kabin memurları, servis yapın', 'Kabin memurları, hazırlanın'] },
  { question: '"Thank you for flying with us" ne demek?', answer: 'Bizimle uçtuğunuz için teşekkürler', options: ['Hoş geldiniz', 'Bizimle uçtuğunuz için teşekkürler', 'Güle güle', 'İyi yolculuklar'] },
  { question: '"We apologize for the delay" ne demek?', answer: 'Gecikme için özür dileriz', options: ['Gecikme yok', 'Gecikme için özür dileriz', 'Erken kalkıyoruz', 'Tam zamanında'] },
  { question: '"Please remain seated until the aircraft comes to a complete stop" ne demek?', answer: 'Uçak tamamen durana kadar oturmaya devam edin', options: ['Hemen kalkın', 'Uçak tamamen durana kadar oturmaya devam edin', 'Bagajınızı alın', 'Kapıya gidin'] },
  { question: '"For your safety" ne demek?', answer: 'Güvenliğiniz için', options: ['Konforunuz için', 'Güvenliğiniz için', 'Eğlenceniz için', 'Rahatınız için'] },
  { question: '"Local time is" ne demek?', answer: 'Yerel saat', options: ['Uçuş süresi', 'Yerel saat', 'Kalkış saati', 'İniş saati'] },
  { question: '"On behalf of the entire crew" ne demek?', answer: 'Tüm ekip adına', options: ['Kaptan adına', 'Tüm ekip adına', 'Şirket adına', 'Pilot adına'] }
];

// All categories configuration
const CATEGORIES = [
  {
    id: 'icao',
    name: 'ICAO Alfabesi',
    description: 'A = Alpha, B = Bravo...',
    icon: '🔤',
    color: 'from-purple-400 to-violet-500',
    badgeColor: 'bg-purple-500',
    questionCount: 26
  },
  {
    id: 'vocabulary',
    name: 'Havacılık Terimleri',
    description: 'Temel uçak ve kabin kelimeleri',
    icon: '📚',
    color: 'from-indigo-400 to-blue-500',
    badgeColor: 'bg-indigo-500',
    questionCount: 20
  },
  {
    id: 'emergency',
    name: 'Acil Durum Prosedürleri',
    description: 'Yangın, tahliye, ilk yardım',
    icon: '🚨',
    color: 'from-red-400 to-orange-500',
    badgeColor: 'bg-red-500',
    questionCount: 20
  },
  {
    id: 'aircraft',
    name: 'Uçak Tipleri',
    description: 'Boeing, Airbus, özellikler',
    icon: '✈️',
    color: 'from-sky-400 to-cyan-500',
    badgeColor: 'bg-sky-500',
    questionCount: 20
  },
  {
    id: 'services',
    name: 'Yolcu Hizmetleri',
    description: 'Özel yolcular, servis, kodlar',
    icon: '👥',
    color: 'from-green-400 to-emerald-500',
    badgeColor: 'bg-green-500',
    questionCount: 20
  },
  {
    id: 'codes',
    name: 'Havalimanı Kodları',
    description: 'IATA kodları ve kısaltmalar',
    icon: '🏷️',
    color: 'from-amber-400 to-yellow-500',
    badgeColor: 'bg-amber-500',
    questionCount: 20
  }
];

export default function CabinCrewTimedChallenge() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [gameMode, setGameMode] = useState(null);
  const [gameState, setGameState] = useState('ready');
  const [timeLeft, setTimeLeft] = useState(60);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [options, setOptions] = useState([]);
  const [answered, setAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [showFeedback, setShowFeedback] = useState(null);
  const [usedQuestions, setUsedQuestions] = useState([]);
  
  const timerRef = useRef(null);

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      timerRef.current = setTimeout(() => {
      // Animation feedback duration
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setGameState('finished');
    }
    
    return () => clearTimeout(timerRef.current);
  }, [gameState, timeLeft]);

  const generateQuestion = () => {
    generateQuestionForMode(gameMode);
  };

  const generateQuestionForMode = (mode) => {
    if (mode === 'icao') {
      const letters = Object.keys(ICAO_ALPHABET);
      const randomLetter = letters[Math.floor(Math.random() * letters.length)];
      const correctAnswer = ICAO_ALPHABET[randomLetter];
      const letterDistractors = ICAO_DISTRACTORS[randomLetter] || [];
      const wrongOptions = letterDistractors
        .filter(v => v.toLowerCase() !== correctAnswer.toLowerCase())
        .sort(() => Math.random() - 0.5)
        .slice(0, 3);
      const allOptions = [...wrongOptions, correctAnswer].sort(() => Math.random() - 0.5);
      setCurrentQuestion({ letter: randomLetter, correct: correctAnswer, type: 'icao' });
      setOptions(allOptions);
    } else if (mode === 'vocabulary') {
      const randomWord = VOCABULARY[Math.floor(Math.random() * VOCABULARY.length)];
      const wrongOptions = VOCABULARY
        .filter(v => v.tr !== randomWord.tr)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
        .map(v => v.tr);
      const allOptions = [...wrongOptions, randomWord.tr].sort(() => Math.random() - 0.5);
      setCurrentQuestion({ word: randomWord.en, correct: randomWord.tr, type: 'vocabulary' });
      setOptions(allOptions);
    } else if (mode === 'emergency') {
      const availableQuestions = EMERGENCY_PROCEDURES.filter((_, i) => !usedQuestions.includes(i));
      const questionsToUse = availableQuestions.length > 0 ? availableQuestions : EMERGENCY_PROCEDURES;
      const randomIndex = Math.floor(Math.random() * questionsToUse.length);
      const q = questionsToUse[randomIndex];
      const originalIndex = EMERGENCY_PROCEDURES.indexOf(q);
      setUsedQuestions(prev => [...prev, originalIndex]);
      setCurrentQuestion({ question: q.question, correct: q.answer, type: 'emergency' });
      setOptions(q.options.sort(() => Math.random() - 0.5));
    } else if (mode === 'aircraft') {
      const availableQuestions = AIRCRAFT_TYPES.filter((_, i) => !usedQuestions.includes(i));
      const questionsToUse = availableQuestions.length > 0 ? availableQuestions : AIRCRAFT_TYPES;
      const randomIndex = Math.floor(Math.random() * questionsToUse.length);
      const q = questionsToUse[randomIndex];
      const originalIndex = AIRCRAFT_TYPES.indexOf(q);
      setUsedQuestions(prev => [...prev, originalIndex]);
      setCurrentQuestion({ question: q.question, correct: q.answer, type: 'aircraft' });
      setOptions(q.options.sort(() => Math.random() - 0.5));
    } else if (mode === 'services') {
      const availableQuestions = PASSENGER_SERVICES.filter((_, i) => !usedQuestions.includes(i));
      const questionsToUse = availableQuestions.length > 0 ? availableQuestions : PASSENGER_SERVICES;
      const randomIndex = Math.floor(Math.random() * questionsToUse.length);
      const q = questionsToUse[randomIndex];
      const originalIndex = PASSENGER_SERVICES.indexOf(q);
      setUsedQuestions(prev => [...prev, originalIndex]);
      setCurrentQuestion({ question: q.question, correct: q.answer, type: 'services' });
      setOptions(q.options.sort(() => Math.random() - 0.5));
    } else if (mode === 'codes') {
      const availableQuestions = AVIATION_CODES.filter((_, i) => !usedQuestions.includes(i));
      const questionsToUse = availableQuestions.length > 0 ? availableQuestions : AVIATION_CODES;
      const randomIndex = Math.floor(Math.random() * questionsToUse.length);
      const q = questionsToUse[randomIndex];
      const originalIndex = AVIATION_CODES.indexOf(q);
      setUsedQuestions(prev => [...prev, originalIndex]);
      setCurrentQuestion({ question: q.question, correct: q.answer, type: 'codes' });
      setOptions(q.options.sort(() => Math.random() - 0.5));
    }
  };

  const startGame = (mode) => {
    setGameMode(mode);
    setGameState('playing');
    setTimeLeft(60);
    setScore(0);
    setStreak(0);
    setMaxStreak(0);
    setAnswered(0);
    setCorrectAnswers(0);
    setShowFeedback(null);
    setUsedQuestions([]);
    
    setTimeout(() => generateQuestionForMode(mode), 100);
  };

  const handleAnswer = (answer) => {
    const isCorrect = answer === currentQuestion.correct;
    setShowFeedback(isCorrect ? 'correct' : 'wrong');
    setAnswered(prev => prev + 1);
    
    if (isCorrect) {
      setCorrectAnswers(prev => prev + 1);
      const newStreak = streak + 1;
      setScore(prev => prev + (10 * (newStreak > 3 ? 2 : 1)));
      setStreak(newStreak);
      if (newStreak > maxStreak) setMaxStreak(newStreak);
    } else {
      setStreak(0);
    }
    
    setTimeout(() => {
      // Animation feedback duration
      setShowFeedback(null);
      generateQuestion();
    }, 1200);  // Increased for animation visibility
  };

  const getCategoryInfo = (modeId) => {
    return CATEGORIES.find(c => c.id === modeId) || CATEGORIES[0];
  };

  // Mode selection screen
  if (!gameMode) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header - Purple Gradient */}
        <div className="bg-gradient-to-r from-purple-500 to-violet-600 pb-8 rounded-b-[2rem]">
          <div className="max-w-4xl mx-auto px-4 pt-4">
            <button
              onClick={() => navigate('/cabin-crew')}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Geri</span>
            </button>
            
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                <Timer className="w-8 h-8 text-purple-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Zamanlı Yarışma</h1>
                <p className="text-purple-100 text-sm">60 saniyede kaç soru çözebilirsin?</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6 -mt-4">
          {/* Category Selection Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIES.map((category) => (
              <Card 
                key={category.id}
                className="cursor-pointer hover:shadow-xl transition-all hover:-translate-y-2 border-0 shadow-lg"
                onClick={() => startGame(category.id)}
              >
                <CardContent className="p-6 text-center">
                  <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-2xl flex items-center justify-center mx-auto mb-3`}>
                    <span className="text-3xl">{category.icon}</span>
                  </div>
                  <h3 className="text-lg font-black text-gray-800 mb-1">{category.name}</h3>
                  <p className="text-gray-500 text-sm mb-3">{category.description}</p>
                  <Badge className={`${category.badgeColor} text-white`}>{category.questionCount} Soru</Badge>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Instructions */}
          <div className="mt-8 text-center text-gray-500 bg-white rounded-xl p-4 shadow">
            <p className="font-medium text-gray-700 mb-2">🎮 Nasıl Oynanır?</p>
            <p>⚡ Hızlı cevap = Daha fazla puan</p>
            <p>🔥 3+ ardışık doğru = 2x puan</p>
            <p>🏆 %50 başarı oranı = Geçiş</p>
          </div>
        </div>
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(gameMode);

  // Finished screen
  if (gameState === 'finished') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className={`bg-gradient-to-r ${categoryInfo.color} pb-8 rounded-b-[2rem]`}>
          <div className="max-w-4xl mx-auto px-4 pt-4">
            <button
              onClick={() => setGameMode(null)}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Geri</span>
            </button>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                <Trophy className="w-8 h-8 text-yellow-500" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Sonuçlar</h1>
                <p className="text-white/80 text-sm">{categoryInfo.name}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-md mx-auto px-4 py-6 -mt-4">
          <Card className="border-0 shadow-lg relative overflow-hidden">
            {/* Confetti for good scores */}
            {answered > 0 && (correctAnswers / answered) >= 0.7 && (
              <div className="absolute inset-0 pointer-events-none">
                <ConfettiAnimation width={400} height={400} className="mx-auto" />
              </div>
            )}
            <CardContent className="p-8 text-center relative z-10">
              {/* Success Animation */}
              <div className="mx-auto mb-4">
                <SuccessAnimation width={150} height={150} />
              </div>
              
              <h2 className="text-3xl font-black text-gray-800 mb-2">
                Süre Doldu! ⏱️
              </h2>
              <p className="text-gray-500 mb-6">{categoryInfo.name}</p>
              
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-green-100 rounded-xl p-3">
                  <Check className="w-6 h-6 text-green-600 mx-auto mb-1" />
                  <p className="text-2xl font-black text-green-600">{correctAnswers}/{answered}</p>
                  <p className="text-xs text-gray-500">Doğru</p>
                </div>
                <div className="bg-purple-100 rounded-xl p-3">
                  <Star className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                  <p className="text-2xl font-black text-purple-600">{score}</p>
                  <p className="text-xs text-gray-500">Puan</p>
                </div>
              </div>
              
              {/* Success Rate */}
              <div className="bg-slate-100 rounded-xl p-3 mb-4">
                <p className="text-center text-gray-600">
                  Başarı: <span className={`font-bold ${answered > 0 && (correctAnswers / answered) >= 0.5 ? 'text-green-600' : 'text-orange-600'}`}>
                    %{answered > 0 ? Math.round((correctAnswers / answered) * 100) : 0}
                  </span>
                </p>
              </div>
              
              <div className="space-y-3">
                {answered > 0 && (correctAnswers / answered) >= 0.5 ? (
                  <>
                    <div className="bg-green-100 border border-green-300 rounded-xl p-3 mb-3">
                      <p className="text-green-700 font-medium text-center">
                        🎉 Tebrikler! Başarılı oldun!
                      </p>
                    </div>
                    <Button
                      onClick={() => setGameMode(null)}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Başka Kategori Seç
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="bg-orange-100 border border-orange-300 rounded-xl p-3 mb-3">
                      <p className="text-orange-700 font-medium text-center">
                        Biraz daha pratik yapalım! (%50 gerekli)
                      </p>
                    </div>
                    <Button
                      onClick={() => startGame(gameMode)}
                      className={`w-full bg-gradient-to-r ${categoryInfo.color}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Tekrar Oyna
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => setGameMode(null)}
                  variant="outline"
                  className="w-full"
                >
                  Farklı Kategori Seç
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Playing screen
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`bg-gradient-to-r ${categoryInfo.color} pb-6 rounded-b-[2rem]`}>
        <div className="max-w-lg mx-auto px-4 pt-4">
          {/* Timer Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 bg-white/20 px-3 py-2 rounded-full">
              <Star className="w-5 h-5 text-yellow-300" />
              <span className="font-bold text-white">{score}</span>
            </div>
            
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
              timeLeft <= 10 ? 'bg-red-500 text-white animate-pulse' : 'bg-white/20 text-white'
            }`}>
              <Timer className="w-5 h-5" />
              <span className="font-black text-2xl">{timeLeft}</span>
            </div>
            
            <div className="flex items-center gap-2 bg-white/20 px-3 py-2 rounded-full">
              <Flame className={`w-5 h-5 ${streak >= 3 ? 'text-orange-300 animate-bounce' : 'text-white/60'}`} />
              <span className={`font-bold ${streak >= 3 ? 'text-orange-300' : 'text-white/60'}`}>{streak}</span>
            </div>
          </div>

          {/* Progress */}
          <div className="h-2 bg-white/30 rounded-full mt-4 overflow-hidden">
            <div 
              className={`h-full transition-all ${timeLeft <= 10 ? 'bg-red-400' : 'bg-white'}`}
              style={{ width: `${(timeLeft / 60) * 100}%` }}
            />
          </div>
          
          {/* Category Badge */}
          <div className="text-center mt-3">
            <Badge className="bg-white/20 text-white border-0">
              {categoryInfo.icon} {categoryInfo.name}
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 -mt-4">
        {/* Question Card */}
        <Card className={`border-0 shadow-xl mb-6 transition-all relative overflow-hidden ${
          showFeedback === 'correct' ? 'ring-4 ring-green-400 bg-green-50' :
          showFeedback === 'wrong' ? 'ring-4 ring-red-400 bg-red-50' :
          ''
        }`}>
          {/* Feedback Animation Overlay */}
          {showFeedback && (
            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
              {showFeedback === 'correct' ? (
                <CorrectAnimation width={120} height={120} />
              ) : (
                <WrongAnimation width={120} height={120} />
              )}
            </div>
          )}
          <CardContent className="p-6 text-center">
            {currentQuestion?.type === 'icao' ? (
              <>
                <p className="text-sm text-gray-500 mb-2">Bu harfin ICAO karşılığı nedir?</p>
                <p className="text-8xl font-black text-purple-600 mb-4">
                  {currentQuestion?.letter}
                </p>
              </>
            ) : currentQuestion?.type === 'vocabulary' ? (
              <>
                <p className="text-sm text-gray-500 mb-2">Bu kelimenin Türkçesi nedir?</p>
                <p className="text-4xl font-black text-indigo-600 mb-4">
                  {currentQuestion?.word}
                </p>
              </>
            ) : (
              <>
                <p className="text-lg font-medium text-gray-800 leading-relaxed">
                  {currentQuestion?.question}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Options */}
        <div className="grid grid-cols-1 gap-3">
          {options.map((option, idx) => (
            <Button
              key={idx}
              onClick={() => handleAnswer(option)}
              disabled={showFeedback !== null}
              className={`h-auto min-h-[48px] py-3 px-4 text-base font-medium transition-all whitespace-normal text-left justify-start ${
                showFeedback && option === currentQuestion.correct
                  ? 'bg-green-500 text-white'
                  : 'bg-white border-2 border-gray-200 text-gray-800 hover:border-purple-400 hover:bg-purple-50'
              }`}
            >
              {option}
            </Button>
          ))}
        </div>

        {/* Streak indicator */}
        {streak >= 3 && (
          <div className="text-center mt-6">
            <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white animate-pulse">
              🔥 {streak}x Seri! 2x Puan!
            </Badge>
          </div>
        )}
      </div>
    </div>
  );
}
